/**
 * Aspects between bodies, for the Visual Universe chart.
 *
 * Pure functions over a list of `{ name, longitude }`. No React, no chart, no network — the part
 * most likely to be subtly wrong is the angular arithmetic, and it is only testable if it does not
 * need a rendered wheel to run.
 */

/**
 * The angles this study looks for.
 *
 * <p><b>270 is deliberately absent.</b> A square is a square whichever body leads: 270 is the same
 * geometric relationship as 90, seen from the other end. Carrying both meant the same pair could
 * be reported as either depending on which body happened to be listed first, so the label told the
 * reader about the iteration order rather than about the sky - three of the seven aspects in the
 * first live run came out as 270 for exactly that reason.
 *
 * <p>Detection is unaffected. It has always folded to [0,180], so a square is found whichever way
 * round it sits; dropping 270 changes only what it is CALLED.
 */
export const ASPECT_ANGLES = [0, 90, 120, 180];

/**
 * The angles the Gann reading uses: the quarters, plus the trine.
 *
 * <p>Identical to {@link ASPECT_ANGLES} by decision, not by accident — the eighths (45 and 135)
 * were tried and dropped. What separates the two readings is therefore not the angle list: Gann
 * carries a wider orb ({@link GANN_ORB} against {@link MAX_ORB}) and shows only aspects that form a
 * configuration, via {@link configurationAspects}.
 *
 * <p>Kept as its own constant rather than aliased to ASPECT_ANGLES so the two can diverge again
 * without a caller silently following whichever one moved.
 *
 * <p><b>240 and 270 need no entries.</b> Detection folds to [0, 180] before comparing, so 240 tests
 * as 120 and 270 as 90 — a trine or a square is found whichever body leads. Adding them would not
 * widen the search by one aspect; it would only change what some of them are CALLED, and would
 * reintroduce the defect where the same square read as 90 or 270 depending on iteration order.
 */
export const GANN_ASPECT_ANGLES = [0, 90, 120, 180];

/** Which named set of angles a scan uses. */
export const ASPECT_SETS = { ALL: 'all', GANN: 'gann' };

/** The angle list for a named set; anything unrecognised falls back to the standard one. */
export function anglesForSet(set) {
  return set === ASPECT_SETS.GANN ? GANN_ASPECT_ANGLES : ASPECT_ANGLES;
}

/** Default distance from an exact aspect for it to count, in degrees. */
export const MAX_ORB = 5;

/**
 * The default orb for the Gann reading: wider, at 7 degrees.
 *
 * <p>Gann's is a geometric reading — the question is whether the bodies are near a quarter or a
 * third of the circle, not whether they are exactly on it — and it is filtered to configurations
 * afterwards, so a wider net feeds that filter more to work with rather than more noise to read.
 */
export const GANN_ORB = 7;

/** The default orb for a named set. */
export function defaultOrbForSet(set) {
  return set === ASPECT_SETS.GANN ? GANN_ORB : MAX_ORB;
}

/**
 * The orb the input accepts.
 *
 * <p>Below 1 the field would need decimals to be usable at all, and the study rounds orbs to whole
 * degrees for display — so a 0.5 orb would report every aspect it found as `(0)` and give the
 * reader no way to tell a tight one from an exact one. Above 10 a square and a trine start
 * overlapping (they are 30 apart, so a 15 orb would let one pair satisfy both), and the tightest
 * match wins silently rather than the pair being reported twice.
 */
export const ORB_MIN = 1;
export const ORB_MAX = 10;

/**
 * An orb forced into range; an input that is not a number keeps the default.
 *
 * <p>The blank check is separate and comes first, because `Number('')` is **0** — finite, and
 * therefore clamped to {@link ORB_MIN} by the range test rather than rejected by the numeric one.
 * A reader clearing the box to retype would have had the orb silently narrowed to 1°, which reads
 * as "almost nothing aspects anything" rather than as "still typing".
 */
export function clampOrb(value) {
  if (value === null || value === undefined || String(value).trim() === '') return MAX_ORB;
  const n = Number(value);
  if (!Number.isFinite(n)) return MAX_ORB;
  return Math.min(ORB_MAX, Math.max(ORB_MIN, Math.round(n)));
}

/** Conventional names, for the tooltip. */
export const ASPECT_NAMES = {
  0: 'Conjunction',
  45: 'Semi-square',
  90: 'Square',
  120: 'Trine',
  135: 'Sesquiquadrate',
  180: 'Opposition',
  // Kept so a caller passing a custom angle list including 270 still gets a name rather than a
  // bare number. Nothing in either default set produces it.
  270: 'Square',
};

/** Short forms for the label, matching the abbreviations the chart overlays already use. */
export const BODY_ABBREVIATIONS = {
  Sun: 'Su', Moon: 'Mo', Mercury: 'Me', Venus: 'Ve', Mars: 'Ma', Jupiter: 'Ju',
  Saturn: 'Sa', Uranus: 'Ur', Neptune: 'Ne', Pluto: 'Pl', Rahu: 'Ra', Ketu: 'Ke',
};

/** `Sa` for Saturn; the first two letters for anything unmapped, so nothing renders blank. */
export function abbreviate(name) {
  if (!name) return '?';
  return BODY_ABBREVIATIONS[name] || String(name).slice(0, 2);
}

/**
 * The label drawn on an aspect line: `Sa-120(1)-Ma`.
 *
 * <p>Both bodies are in it because a square chart carries a dozen lines crossing the same space,
 * and a label reading only `120(1)` cannot be traced back to the pair it belongs to once two
 * lines pass near each other. The orb is in whole degrees, per the study's formatting rule.
 */
export function aspectLabel(aspect) {
  if (!aspect) return '';
  return `${abbreviate(aspect.a)}-${aspect.angle}(${Math.round(aspect.orb)})-${abbreviate(aspect.b)}`;
}

/**
 * The same reading split into its four columns: `[P1] [Aspect°] [(Orb°)] [P2]`.
 *
 * <p>A separate function rather than parsing {@link aspectLabel} back apart. The single-string form
 * is what the chart draws, where one label follows one line and self-alignment is meaningless; a
 * LIST of them wants its angles under its angles, which needs the fields kept whole rather than
 * recovered from punctuation that could legitimately appear in a body name.
 *
 * <p>Every field is a string, already padded to a fixed width, so the caller can lay them out with
 * a grid or a monospace run and get the same answer either way. Widths come from the worst case
 * each column can produce: two characters for an abbreviation, three digits for 180, and two for an
 * orb capped at {@link ORB_MAX}.
 */
export function aspectLabelParts(aspect) {
  if (!aspect) return null;
  return {
    p1: abbreviate(aspect.a).padEnd(2),
    angle: `${aspect.angle}°`.padStart(4),
    orb: `(${Math.round(aspect.orb)}°)`.padStart(5),
    p2: abbreviate(aspect.b).padEnd(2),
  };
}

/**
 * Pairs whose aspect is a definition rather than an observation.
 *
 * <p>Ketu IS Rahu plus 180 degrees — it is not measured, it is derived, and the two can no more
 * drift apart than a point can drift from its own antipode. So "Rahu opposite Ketu, orb 0" is
 * true on every bar of every chart in every frame, and reporting it is noise that crowds out the
 * aspects that carry information. It was the first line of the live output before this existed.
 *
 * <p>Held as sorted, lower-cased name pairs so the lookup does not depend on which body the
 * caller happened to list first. The node aliases are included because saved studies and older
 * paths spell them several ways.
 */
export const STRUCTURAL_PAIRS = [
  ['ketu', 'rahu'],
  ['north node', 'south node'],
  ['rahu', 'south node'],
  ['ketu', 'north node'],
];

/** True when a pair is opposed by definition, so its aspect says nothing about the sky. */
export function isStructuralPair(a, b) {
  if (!a || !b) return false;
  const key = [String(a).trim().toLowerCase(), String(b).trim().toLowerCase()].sort();
  return STRUCTURAL_PAIRS.some(pair => pair[0] === key[0] && pair[1] === key[1]);
}

/**
 * Only the aspects that form a configuration: those touching a body that has more than one.
 *
 * <p>A pair aspecting each other and nothing else is an isolated fact. A body carrying two or more
 * — a trine and a conjunction, a trine and an opposition — is a hub, and the aspects through it
 * describe a shape rather than a coincidence. That is the reading the Gann set is for, and the set
 * is wide enough (six angles rather than four) that without this the wheel fills with unrelated
 * pairs and the shapes stop being visible in the crowd.
 *
 * <p>Kept if EITHER endpoint qualifies, not both. An aspect from a hub out to a body that has only
 * that one is still part of the hub's shape; requiring both ends to be hubs would sever every
 * outer arm and leave only the core, which is a different and much smaller claim.
 *
 * <p>Degree is counted over the aspects PASSED IN, so a filtered list re-filtered does not shrink
 * again — the count must describe the sky, not the previous filter's output.
 */
export function configurationAspects(aspects) {
  if (!Array.isArray(aspects)) return [];

  const degree = new Map();
  const bump = name => degree.set(name, (degree.get(name) || 0) + 1);
  aspects.forEach(a => { bump(a.a); bump(a.b); });

  return aspects.filter(a => (degree.get(a.a) || 0) > 1 || (degree.get(a.b) || 0) > 1);
}

/** Positive modulus, so a negative separation wraps rather than staying negative. */
function mod360(value) {
  return ((value % 360) + 360) % 360;
}

/**
 * How far `b` lies ahead of `a`, measured forwards: [0, 360).
 *
 * Directed rather than folded, because 90 and 270 are both in the requested angle list and the
 * folded distance cannot tell them apart — see {@link findAspects}.
 */
export function directedSeparation(a, b) {
  return mod360(b - a);
}

/**
 * The angular DISTANCE between two longitudes, folded to [0, 180].
 *
 * 350 degrees apart one way is 10 the other, and an aspect does not care which body leads.
 */
export function angularDistance(a, b) {
  const raw = mod360(b - a);
  return raw > 180 ? 360 - raw : raw;
}

/**
 * The aspect between two longitudes, or null.
 *
 * <h2>Detection folds to [0, 180]</h2>
 * A pair is tested on its angular DISTANCE, not on the directed separation between them, so an
 * aspect is found whichever body leads. Measured directionally instead, a trine where the second
 * body leads is a separation of 240 — and with 240 absent from any sensible angle list, half of
 * all trines would be missed silently.
 *
 * <p>Folding is also why 270 is no longer in {@link ASPECT_ANGLES}: it tests as 90 and describes
 * the same geometry. The promotion below survives only for a caller that passes 270 explicitly.
 *
 * @param {number} a longitude of the first body, degrees
 * @param {number} b longitude of the second body, degrees
 * @param {number[]} [angles] aspect angles to look for
 * @param {number} [maxOrb] tolerance in degrees
 * @returns {{angle:number, orb:number, exact:boolean, name:string, separation:number}|null}
 */
export function aspectBetween(a, b, angles = ASPECT_ANGLES, maxOrb = MAX_ORB) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

  const separation = directedSeparation(a, b);
  const distance = angularDistance(a, b);

  let best = null;
  for (const angle of angles) {
    // The folded target: 270 tests as 90, which is what makes a square match either way round.
    const target = angle > 180 ? 360 - angle : angle;
    const orb = Math.abs(distance - target);
    if (orb > maxOrb) continue;
    if (best === null || orb < best.orb) {
      best = { angle, orb, target };
    }
  }
  if (best === null) return null;

  // Between 90 and 270 the directed separation decides. Only reached when both are in the angle
  // list; with either alone, that one is reported and the caller gets what it asked for.
  let angle = best.angle;
  const hasPartner = angles.includes(90) && angles.includes(270);
  if (hasPartner && (best.target === 90)) {
    angle = separation < 180 ? 90 : 270;
  }

  return {
    angle,
    orb: best.orb,
    // Rounded to whole degrees for display, per the study's own formatting rule. Kept alongside
    // the precise value rather than replacing it, so a caller that wants to sort or threshold on
    // orb is not doing it on a rounded number.
    orbLabel: `${Math.round(best.orb)}°`,
    exact: Math.round(best.orb) === 0,
    name: ASPECT_NAMES[angle] || `${angle}°`,
    separation,
  };
}

/**
 * Every aspect among the given bodies.
 *
 * <p>Each unordered PAIR is tested once. Testing both orders would report every aspect twice — and
 * worse, would report the same square as 90 in one direction and 270 in the other, which reads as
 * two separate findings about the same two bodies.
 *
 * @param {Array<{name:string, longitude:number}>} positions
 * @returns {Array<{a:string, b:string, angle:number, orb:number, orbLabel:string, name:string}>}
 *          sorted by tightest orb first, so the strongest aspects draw on top
 */
export function findAspects(positions, angles = ASPECT_ANGLES, maxOrb = MAX_ORB) {
  if (!Array.isArray(positions)) return [];

  const usable = positions.filter(p => p && Number.isFinite(p.longitude));
  const out = [];
  for (let i = 0; i < usable.length; i++) {
    for (let j = i + 1; j < usable.length; j++) {
      // Skipped before the aspect is computed, not filtered out after: a pair opposed by
      // definition has no aspect to compute, and testing it would only ever produce the same
      // answer. See STRUCTURAL_PAIRS.
      if (isStructuralPair(usable[i].name, usable[j].name)) continue;
      const found = aspectBetween(usable[i].longitude, usable[j].longitude, angles, maxOrb);
      if (!found) continue;
      out.push({
        a: usable[i].name,
        b: usable[j].name,
        aLongitude: usable[i].longitude,
        bLongitude: usable[j].longitude,
        ...found,
      });
    }
  }
  return out.sort((x, y) => x.orb - y.orb);
}
